import { Field } from "@ui/components/Field";
import { Icon } from "@ui/components/Icon/Icon";
import { ListSectionHeader } from "@ui/components/internal/ListSectionHeader";
import { searchableListBox } from "@ui/components/internal/SearchableListBox";
import { ScrollArea } from "@ui/components/ScrollArea";
import { tv } from "@ui/lib/tv";
import { Autocomplete, type AutocompleteProps, SearchField, SearchFieldInput } from "@ui/primitives/Autocomplete";
import { ListBox, ListBoxHeader, ListBoxItem, type ListBoxItemProps, ListBoxSection } from "@ui/primitives/ListBox";
import { composeRenderProps } from "@ui/primitives/shared";
import type { ReactNode } from "react";
import TornPaperSvg from "./torn-paper.svg?react";

const standardAutocomplete = tv({
  slots: {
    container: ["absolute left-0 right-0 top-full z-50 mt-1 bg-transparent", "drop-shadow-popover"],
    containerInner: "relative overflow-hidden rounded-t-[10px]",
    containerBg: "pointer-events-none absolute inset-0 h-full w-full",
    tornEdgeSpacer: "relative h-3",
    itemText: "truncate",
  },
});

export interface StandardAutocompleteItem {
  id: string;
  name: string;
  description?: string | null;
}

export interface StandardAutocompleteSection {
  id: string;
  /** Section header label. Omit to render the section's items flush, without a label row. */
  title?: string;
  items: StandardAutocompleteItem[];
}

/** Controlled state props for StandardAutocomplete, suitable for passing through component layers. */
export interface StandardAutocompleteControlledProps {
  inputValue: string;
  sections: StandardAutocompleteSection[];
  isLoading: boolean;
  onInputChange: (value: string) => void;
  onSelectionChange: (id: string | null) => void;
  selectedItem: StandardAutocompleteItem | null;
}

export interface StandardAutocompleteProps extends Omit<AutocompleteProps<object>, "children" | "filter"> {
  label: string;
  isOptional?: boolean;
  isInvalid?: boolean;
  errorMessage?: ReactNode;
  leadIcon?: ReactNode;
  placeholder?: string;
  sections: StandardAutocompleteSection[];
  onSelectionChange: (id: string | null) => void;
  isLoading?: boolean;
  selectedItem?: StandardAutocompleteItem | null;
}

export function StandardAutocomplete({
  label,
  isOptional,
  isInvalid,
  errorMessage,
  leadIcon,
  placeholder,
  sections,
  onSelectionChange,
  isLoading,
  selectedItem,
  inputValue,
  onInputChange,
  ...props
}: StandardAutocompleteProps) {
  const styles = standardAutocomplete();
  const listStyles = searchableListBox({ icon: "sm", tint: "solid" });
  const showList = !selectedItem && (inputValue?.length ?? 0) >= 2;

  const handleSelectionChange = (key: React.Key | null) => {
    if (key === null) {
      onSelectionChange(null);
      return;
    }
    const id = String(key);
    const item = sections.flatMap((s) => s.items).find((i) => i.id === id);
    if (item) {
      onInputChange?.(item.name);
    }
    onSelectionChange(id);
  };

  return (
    <Field className="relative" isInvalid={isInvalid} isOptional={isOptional}>
      {/* `disableAutoFocusFirst`: Autocomplete otherwise re-focuses the first
          option every time the collection changes, and scrolling that option
          into view inside the surrounding dialog changes it again — results
          arrive asynchronously here, so the list is already open when they
          land and the two wedge the main thread. Arrow keys still reach the
          list; only the pre-highlighted first row goes. */}
      <Autocomplete
        {...props}
        disableAutoFocusFirst
        filter={undefined}
        inputValue={inputValue}
        onInputChange={onInputChange}
      >
        <SearchField aria-label={label} className={listStyles.searchField()}>
          <Field.Label>{label}</Field.Label>
          <Field.Control asGroup leadIcon={leadIcon}>
            <SearchFieldInput className={listStyles.searchInput()} placeholder={placeholder} />
          </Field.Control>
          {isInvalid && errorMessage && <Field.Error>{errorMessage}</Field.Error>}
        </SearchField>
        {showList && (
          <div className={styles.container()}>
            <div className={styles.containerInner()}>
              <TornPaperSvg aria-hidden="true" className={styles.containerBg()} preserveAspectRatio="none" />
              <ScrollArea
                className="relative max-h-[296px]"
                render={
                  <ListBox
                    aria-label={`${label} suggestions`}
                    onAction={handleSelectionChange}
                    renderEmptyState={() => (
                      <div className={listStyles.empty()}>{isLoading ? "Searching..." : "No results found"}</div>
                    )}
                    selectionMode="none"
                  />
                }
                viewportClassName={listStyles.list({ className: "gap-3 px-5 pb-2 pt-5" })}
              >
                {sections
                  .filter((section) => section.items.length > 0)
                  .map((section) => (
                    <ListBoxSection key={section.id} id={section.id}>
                      {section.title ? (
                        <ListBoxHeader>
                          <ListSectionHeader>{section.title}</ListSectionHeader>
                        </ListBoxHeader>
                      ) : null}
                      {section.items.map((item) => (
                        <StandardAutocompleteListItem key={item.id} item={item} styles={styles} />
                      ))}
                    </ListBoxSection>
                  ))}
              </ScrollArea>
              <div className={styles.tornEdgeSpacer()} />
            </div>
          </div>
        )}
      </Autocomplete>
    </Field>
  );
}

interface StandardAutocompleteListItemProps extends Omit<ListBoxItemProps, "children"> {
  item: StandardAutocompleteItem;
  styles: ReturnType<typeof standardAutocomplete>;
}

function StandardAutocompleteListItem({ item, styles, ...props }: StandardAutocompleteListItemProps) {
  const listStyles = searchableListBox({ icon: "sm", tint: "solid" });
  return (
    <ListBoxItem
      {...props}
      id={item.id}
      textValue={item.name}
      className={composeRenderProps(props.className, (className) =>
        // Rows bleed 8px into the list's px-5 gutter so the hover pill outsizes the text column.
        listStyles.item({ className: ["-mx-2 text-base leading-6 text-neutral-600", className] }),
      )}
    >
      <span className={listStyles.itemIcon()}>
        <Icon name="location" size="16" />
      </span>
      <span className={styles.itemText()}>{item.description || item.name}</span>
    </ListBoxItem>
  );
}
